import { Injectable } from '@angular/core';
import moment from 'moment/moment';
import { combineLatest, ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { oneResultFrom, oneResultFromEach } from '@vendasta/rx-utils';
import { ProductAnalyticsService } from '@vendasta/product-analytics';

class DataSchema {
  app_id: string;
  feedback_value: number;
  feedback_message: string;
  feedback_identifier: string;
  feedback_version: string;
}

export interface IdentifierSchema {
  version: string;
  timesInteractedWith: number;
  feedbackReceivedForVersion: boolean;
  userClickedRemindLater: number;
}

export const DEFAULT_TIMES_INTERACTED_TO_PROMPT = 15;
export const DAYS_TO_WAIT_BEFORE_REMINDING = 2;
const VERSION_REGEX = /^[0-9]+\.[0-9]+\.[0-9]+$/;
export const IDENTIFIER_MAX_LENGTH = 64;
export const IDENTIFIER_MIN_LENGTH = 20;
export type FEEDBACK_POSITION =
  | 'TOP_LEFT'
  | 'BOTTOM_LEFT'
  | 'TOP_RIGHT'
  | 'BOTTOM_RIGHT'
  | 'BOTTOM_CENTER'
  | 'TOP_CENTER';
export type FEEDBACK_SIZE = 'small' | 'medium' | 'large';

@Injectable()
export class FeedbackService {
  private readonly feedbackIdentifier$$ = new ReplaySubject<string>(1);

  set feedbackIdentifier(value: string) {
    this.feedbackIdentifier$$.next(value);
  }

  readonly feedbackIdentifier$ = this.feedbackIdentifier$$.asObservable();

  private readonly feedbackVersion$$ = new ReplaySubject<string>(1);

  set feedbackVersion(value: string) {
    this.feedbackVersion$$.next(value);
  }

  readonly feedbackVersion$ = this.feedbackVersion$$.asObservable();

  interactionsToPrompt: number = DEFAULT_TIMES_INTERACTED_TO_PROMPT;

  private readonly feedbackReceived$$ = new ReplaySubject<boolean>(1);
  readonly feedbackReceived$ = this.feedbackReceived$$.asObservable();

  private readonly versionValid$$ = new ReplaySubject<boolean>(1);
  readonly versionValid$ = this.versionValid$$.asObservable();

  private readonly identifierValid$$ = new ReplaySubject<boolean>(1);
  readonly identifierValid$ = this.identifierValid$$.asObservable();

  private readonly reachedInteractionThreshold$$ = new ReplaySubject<boolean>(1);
  readonly reachedInteractionThreshold$ = this.reachedInteractionThreshold$$.asObservable();

  private readonly remindUserNow$$ = new ReplaySubject<boolean>(1);
  readonly remindUserNow$ = this.remindUserNow$$.asObservable();

  constructor(private readonly productAnalyticsService: ProductAnalyticsService) {}

  initIdentifier(): void {
    this.checkVersionRegex();

    combineLatest([this.feedbackIdentifier$, this.feedbackVersion$])
      .pipe(take(1))
      .subscribe(([identifier, version]) => {
        this.checkRequiredFields(identifier, version);
        this.checkIdentifierLength(identifier);
      });

    this.initializeIdentifier().then((info) => {
      this.feedbackReceived$$.next(info.feedbackReceivedForVersion);
      this.reachedInteractionThreshold$$.next(info.timesInteractedWith >= this.interactionsToPrompt);

      if (info.userClickedRemindLater !== null) {
        const reminderSetDate = moment(new Date(info.userClickedRemindLater))
          .add(DAYS_TO_WAIT_BEFORE_REMINDING, 'days')
          .valueOf();
        this.remindUserNow$$.next(reminderSetDate < Date.now().valueOf() && info.feedbackReceivedForVersion === false);
      } else {
        this.remindUserNow$$.next(true);
      }
    });
  }

  /**
   * Submits user feedback to snowplow under the custom event `user_feedback_tracking`
   * @param feedbackValue: The value of the feedback from 1-3, where 3 is highest
   * @param feedbackMessage: The feedback message that the user entered
   * @param feedbackIdentifierOverride: The ID to associate the feedback with (Optional: to be used when bypassing the visible/delayed components)
   * @param feedbackVersionOverride: The version to associate the feedback with (Optional: to be used when bypassing the visible/delayed components)
   */
  submitFeedback(
    feedbackValue: number,
    feedbackMessage?: string,
    feedbackIdentifierOverride?: string,
    feedbackVersionOverride?: string,
  ): void {
    if (!!feedbackIdentifierOverride && !!feedbackVersionOverride) {
      const data = <DataSchema>{
        feedback_message: feedbackMessage,
        feedback_version: feedbackVersionOverride,
      };
      this.productAnalyticsService.trackEvent(
        'user-feedback-submitted',
        feedbackIdentifierOverride,
        'submit-feedback',
        feedbackValue,
        data,
      );
    } else {
      this.feedbackIdentifier$$.pipe(take(1)).subscribe((feedbackIdentifier) => {
        const currentVersion = this.getFeedbackInfoFromStorage(feedbackIdentifier).version;
        const data = <DataSchema>{
          feedback_message: feedbackMessage,
          feedback_version: currentVersion,
        };
        this.productAnalyticsService.trackEvent(
          'user-feedback-submitted',
          feedbackIdentifier,
          'submit-feedback',
          feedbackValue,
          data,
        );
        this.setFeedbackReceived(feedbackIdentifier);
      });
    }
  }

  checkVersionRegex(): void {
    oneResultFrom(this.feedbackVersion$).then((version) => {
      const found = version.match(VERSION_REGEX);

      if (!found) {
        throw new Error("feedbackVersion must match the format '0.0.0'");
      }
    });
  }

  private async initializeIdentifier(): Promise<IdentifierSchema> {
    const results = oneResultFromEach([this.feedbackIdentifier$, this.feedbackVersion$]);
    return await Promise.all(results).then(([identifier, version]) => {
      let info: IdentifierSchema;
      if (this.identifierExists(identifier) === false) {
        info = this.writeNewIdentifierToStorage(identifier, version);
      } else {
        info = this.getFeedbackInfoFromStorage(identifier);
        if (info.version !== version) {
          info = this.updateFeedbackVersionInStorage(identifier, version);
        }
      }
      return info;
    });
  }

  incrementTimesInteracted(): void {
    oneResultFrom(this.feedbackIdentifier$).then((identifier) => {
      const currentValues = this.getFeedbackInfoFromStorage(identifier);

      currentValues.timesInteractedWith = currentValues.timesInteractedWith + 1;
      this.writeFeedbackInfoToStorage(identifier, currentValues);
      this.reachedInteractionThreshold$$.next(currentValues.timesInteractedWith >= this.interactionsToPrompt);
    });
  }

  private updateFeedbackVersionInStorage(feedbackIdentifier: string, version: string): IdentifierSchema {
    const currentValues = this.getFeedbackInfoFromStorage(feedbackIdentifier);

    currentValues.version = version;
    currentValues.timesInteractedWith = 0;
    currentValues.feedbackReceivedForVersion = false;
    currentValues.userClickedRemindLater = null;

    this.writeFeedbackInfoToStorage(feedbackIdentifier, currentValues);
    return currentValues;
  }

  private identifierExists(feedbackIdentifier: string): boolean {
    return localStorage.getItem(feedbackIdentifier) !== null;
  }

  private setFeedbackReceived(feedbackIdentifier: string): void {
    const currentValues = this.getFeedbackInfoFromStorage(feedbackIdentifier);
    currentValues.feedbackReceivedForVersion = true;
    this.writeFeedbackInfoToStorage(feedbackIdentifier, currentValues);
    this.feedbackReceived$$.next(true);
  }

  private writeNewIdentifierToStorage(feedbackIdentifier: string, version: string): IdentifierSchema {
    const json: IdentifierSchema = {
      version: version,
      timesInteractedWith: 0,
      feedbackReceivedForVersion: false,
      userClickedRemindLater: null,
    };

    this.writeFeedbackInfoToStorage(feedbackIdentifier, json);
    return json;
  }

  remindMeLater(): void {
    oneResultFrom(this.feedbackIdentifier$).then((identifier) => {
      const currentValues = this.getFeedbackInfoFromStorage(identifier);
      currentValues.userClickedRemindLater = Date.now();
      this.writeFeedbackInfoToStorage(identifier, currentValues);
      const reminderSetDate = moment(new Date(currentValues.userClickedRemindLater))
        .add(DAYS_TO_WAIT_BEFORE_REMINDING, 'days')
        .valueOf();
      this.remindUserNow$$.next(reminderSetDate < Date.now().valueOf());
    });
  }

  private writeFeedbackInfoToStorage(feedbackIdentifier: string, values: IdentifierSchema): void {
    localStorage.setItem(feedbackIdentifier, JSON.stringify(values));
    this.feedbackReceived$$.next(values.feedbackReceivedForVersion);
  }

  private getFeedbackInfoFromStorage(feedbackIdentifier: string): IdentifierSchema {
    const jsonString = localStorage.getItem(feedbackIdentifier);

    // Handle old values in localstorage
    if (jsonString?.match(VERSION_REGEX)) {
      localStorage.removeItem(feedbackIdentifier);
      this.writeNewIdentifierToStorage(feedbackIdentifier, jsonString);
      return JSON.parse(localStorage.getItem(feedbackIdentifier));
    }

    return JSON.parse(jsonString);
  }

  private checkRequiredFields(featureId: string, version: string): void {
    if (featureId === null) {
      this.identifierValid$$.next(false);
    }
    if (version === null || !version.match(VERSION_REGEX)) {
      this.versionValid$$.next(false);
    } else {
      this.versionValid$$.next(true);
    }
  }

  private checkIdentifierLength(identifier: string): void {
    if (identifier === null || identifier.length > IDENTIFIER_MAX_LENGTH || identifier.length < IDENTIFIER_MIN_LENGTH) {
      this.identifierValid$$.next(false);
      throw new Error(
        `featureID must be set and have a length of ${IDENTIFIER_MIN_LENGTH} to ` +
          `${IDENTIFIER_MAX_LENGTH} characters. ` +
          `(Found "${identifier}", length: [${identifier ? identifier.length : 0}])`,
      );
    } else {
      this.identifierValid$$.next(true);
    }
  }
}
